// The OFFSHORELABS desktop app (operator decision 2026-10-31, rows C362 and C378). A person pairs the app on a Windows or
// macOS computer; the server keeps a hashed token per device (a `tokens` row, kind "device"). The app reports the
// computer's inventory to the person's equipment record and, where the workspace's policy turns it on, the activity of
// hourly work: per tracked minute only how many keyboard and mouse events happened (never which keys), optionally the
// active app and window title and a screenshot. These shapes are shared by the server (mod-people devices.ts, mod-time
// activity.ts, apps/server/src/routes/device.ts), the web and apps/desktop/src/shared/protocol.ts (a copy, kept in step).
import { z } from "zod";

export const DEVICE_PLATFORM = z.enum(["win32", "darwin", "linux"]);
export type DevicePlatform = z.infer<typeof DEVICE_PLATFORM>;

/** What a paired computer is now: in use, unpaired by its person or an owner, or handed back at offboarding. */
export const DEVICE_STATUS = z.enum(["active", "revoked", "returned"]);
export type DeviceStatus = z.infer<typeof DEVICE_STATUS>;

/** The app's description of itself when it pairs. */
export const DEVICE_HELLO = z.strictObject({
  name: z.string().trim().min(1).max(120),
  platform: DEVICE_PLATFORM,
  appVersion: z.string().trim().min(1).max(40),
  hostname: z.string().trim().max(255).optional(),
});
export type DeviceHello = z.infer<typeof DEVICE_HELLO>;

/** POST /api/device/pair: a one-time code from Settings → Devices (or the browser sign-in page) and the app's hello. */
export const DEVICE_PAIR = z.strictObject({ code: z.string().trim().min(8).max(20), device: DEVICE_HELLO });
export type DevicePair = z.infer<typeof DEVICE_PAIR>;

/** POST /api/device/inventory: what the app read from the computer. A field the app could not read is left out (or null
 *  for disk encryption: on, off, or not known). Sizes are bytes. */
export const DEVICE_INVENTORY = z.strictObject({
  maker: z.string().trim().max(120).optional(),
  model: z.string().trim().max(160).optional(),
  serial: z.string().trim().max(120).optional(),
  osName: z.string().trim().max(120).optional(),
  osVersion: z.string().trim().max(120).optional(),
  cpu: z.string().trim().max(200).optional(),
  cpuCores: z.number().int().min(1).max(1024).optional(),
  ramBytes: z.number().int().min(0).max(2 ** 50).optional(),
  diskBytes: z.number().int().min(0).max(2 ** 56).optional(),
  diskFreeBytes: z.number().int().min(0).max(2 ** 56).optional(),
  diskEncrypted: z.boolean().nullable().optional(),
  hostname: z.string().trim().max(255).optional(),
  appVersion: z.string().trim().max(40).optional(),
});
export type DeviceInventory = z.infer<typeof DEVICE_INVENTORY>;

/** POST /api/device/heartbeat: the app is alive, and what it is tracking now (null when the timer is stopped). */
export const DEVICE_HEARTBEAT = z.strictObject({
  tracking: z.strictObject({ projectId: z.string().min(1).max(80), taskId: z.string().min(1).max(80).optional(), since: z.iso.datetime({ offset: true }) }).nullable(),
});
export type DeviceHeartbeat = z.infer<typeof DEVICE_HEARTBEAT>;

/** A device as the web and the app read it. */
export type DeviceView = {
  id: string;
  personId: string;
  name: string;
  platform: DevicePlatform;
  appVersion: string;
  hostname: string | null;
  maker: string | null;
  model: string | null;
  serial: string | null;
  osName: string | null;
  osVersion: string | null;
  cpu: string | null;
  cpuCores: number | null;
  ramBytes: number | null;
  diskBytes: number | null;
  diskFreeBytes: number | null;
  /** null: the app could not tell (no inventory yet, or the OS did not say). */
  diskEncrypted: boolean | null;
  inventoryAt: string | null;
  lastSeenAt: string | null;
  tracking: { projectId: string; taskId?: string; since: string } | null;
  /** The person agreed to the workspace's current activity policy on this device. */
  consentCurrent: boolean;
  pairedAt: string;
  status: DeviceStatus;
  revokedAt: string | null;
  returnedAt: string | null;
  returnNote: string | null;
};

/** What the owner decides the desktop app captures (Settings → Time → Desktop app). Nothing is captured while `enabled`
 *  is off; key contents are never captured, whatever the policy says. */
export const DESKTOP_ACTIVITY = z.strictObject({
  enabled: z.boolean(),
  /** Whose time the app tracks: freelancers only, or every member who pairs the app. */
  appliesTo: z.enum(["freelancers", "everyone"]),
  captureApps: z.boolean(),
  /** Window titles can hold client names and document titles, so they are a separate choice from app names. */
  captureTitles: z.boolean(),
  screenshots: z.enum(["off", "blurred", "clear"]),
  screenshotEveryMinutes: z.number().int().min(5).max(60),
  /** Minutes with no keyboard or mouse input before the app pauses and asks whether to keep the idle time. */
  idleMinutes: z.number().int().min(1).max(60),
  /** Websites: the host of the browser's address bar in front, never its path or query. Optional, so a policy saved before this
   *  field existed still reads as off. */
  capturesSites: z.boolean().optional(),
});
export type DesktopActivity = z.infer<typeof DESKTOP_ACTIVITY>;
export const DEFAULT_DESKTOP_ACTIVITY: DesktopActivity = {
  enabled: false,
  appliesTo: "freelancers",
  captureApps: true,
  captureTitles: false,
  screenshots: "off",
  screenshotEveryMinutes: 10,
  idleMinutes: 5,
  capturesSites: false,
};

/** Seconds per website in one minute (at most 60 each, at most 50 websites). A host is a lower-case host name with `www.`
 *  removed, or "(browser)" when the browser's address could not be read. */
export const ACTIVITY_SITES = z
  .record(z.string().min(1).max(253).regex(/^(\(browser\)|[a-z0-9-]+(\.[a-z0-9-]+)*)$/), z.number().int().min(0).max(60))
  .refine((s) => Object.keys(s).length <= 50, "At most 50 websites in one minute.");

/** One tracked minute: how many keyboard and mouse events happened in it. Never which keys. `sites` only when the policy
 *  captures websites. */
export const ACTIVITY_MINUTE = z.strictObject({
  k: z.number().int().min(0).max(100_000),
  m: z.number().int().min(0).max(100_000),
  sites: ACTIVITY_SITES.optional(),
});
export type ActivityMinute = z.infer<typeof ACTIVITY_MINUTE>;

/** Time spent in one app during a slot; the title only when the policy captures titles. */
export const ACTIVITY_APP = z.strictObject({
  app: z.string().trim().min(1).max(120),
  title: z.string().trim().max(300).optional(),
  seconds: z.number().int().min(0).max(600),
});
export type ActivityApp = z.infer<typeof ACTIVITY_APP>;

/** A slot: up to ten tracked minutes on one task or project, as the contractor kept it after review in the app. `id` is the
 *  app's own id, so a submit that is sent twice stores the slot once. A screenshot is declared here by its SHA-256 and sent
 *  after the slot with PUT /api/device/activity/:id/shot. */
export const ACTIVITY_SLOT = z.strictObject({
  id: z.string().regex(/^[A-Za-z0-9_-]{8,64}$/),
  projectId: z.string().min(1).max(80),
  taskId: z.string().min(1).max(80).optional(),
  startedAt: z.iso.datetime({ offset: true }),
  minutes: z.array(ACTIVITY_MINUTE).min(1).max(10),
  apps: z.array(ACTIVITY_APP).max(20).optional(),
  screenshot: z.strictObject({ sha256: z.string().regex(/^[0-9a-f]{64}$/), blurred: z.boolean() }).optional(),
  /** What the contractor says they worked on; it becomes the time entry's note. A break (N2-DESK-E: "Keep as break" on idle time)
   *  is kept as its own, non-billable entry and is not work. Absent = work. */
  memo: z.string().trim().max(500).optional(),
  kind: z.enum(["work", "break"]).optional(),
});
export type ActivitySlot = z.infer<typeof ACTIVITY_SLOT>;

export const ACTIVITY_SUBMIT = z.strictObject({ slots: z.array(ACTIVITY_SLOT).min(1).max(144) });
export type ActivitySubmit = z.infer<typeof ACTIVITY_SUBMIT>;

/** What happened to each submitted slot. */
export type ActivitySubmitResult = {
  slots: { id: string; result: "stored" | "duplicate"; timeEntryId: string; screenshot: "expected" | "refused" | "none"; note?: string }[];
  entries: { id: string; date: string; projectId: string; taskKey: string | null; minutes: number }[];
};

/** A slot as the work diary shows it. Activity is computed on read from the minutes. */
export type DiarySlot = {
  id: string;
  startedAt: string;
  endedAt: string;
  projectId: string;
  projectName: string;
  taskId: string | null;
  taskKey: string | null;
  taskTitle: string | null;
  trackedMinutes: number;
  activeMinutes: number;
  activityPct: number;
  keys: number;
  mouse: number;
  apps: ActivityApp[];
  /** Seconds per website host over the slot's minutes, when the policy captured websites (absent otherwise). */
  sites?: Record<string, number>;
  memo: string | null;
  screenshot: { url: string; blurred: boolean } | null;
  /** SHA-256 of the stored screenshot's bytes, when one was received: the same shot shows the same hash. */
  shotSha256?: string;
  timeEntryId: string | null;
  entryState: string | null;
  deviceId: string;
  /** "break" slots are greyed in the diary and are not billable (N2-DESK-E). Absent = work. */
  kind?: "work" | "break";
};

/** GET /activity/diary?personId=&date=: one person's day of submitted desktop activity. */
export type ActivityDiary = {
  personId: string;
  date: string;
  timeZone: string;
  slots: DiarySlot[];
  /** Work slots only; break minutes are reported apart in breakMinutes (N2-DESK-E). */
  totals: { trackedMinutes: number; activeMinutes: number; activityPct: number; screenshots: number; breakMinutes?: number };
  /** The caller may delete these slots: their own, while the time is not approved or invoiced. */
  canDelete: boolean;
};

/** The version of a policy the person consents to: FNV-1a over the policy's fields in a fixed order, as 8 hex digits. Any
 *  change to what is captured gives a new version, so the app asks for consent again. Pure, so the web, the server and the
 *  app compute the same string. */
export function desktopPolicyVersion(p: DesktopActivity): string {
  const parts: (string | number | boolean)[] = [p.enabled, p.appliesTo, p.captureApps, p.captureTitles, p.screenshots, p.screenshotEveryMinutes, p.idleMinutes];
  // websites are appended only when on, so a policy that never captured them keeps the version it was consented to
  if (p.capturesSites) parts.push("sites");
  const text = parts.join("|");
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, "0");
}

/** Whether the policy tracks this kind of member: freelancers only, or everyone who pairs the app. */
export const desktopTracks = (p: DesktopActivity, kind: string | null | undefined): boolean =>
  p.enabled && (p.appliesTo === "everyone" || kind === "freelancer");

/** Activity of a set of minutes: the share of tracked minutes that had any keyboard or mouse input, 0 to 100. */
export function activityOf(minutes: readonly ActivityMinute[]): { tracked: number; active: number; pct: number; keys: number; mouse: number } {
  const tracked = minutes.length;
  const active = minutes.filter((x) => x.k + x.m > 0).length;
  return {
    tracked,
    active,
    pct: tracked ? Math.round((active / tracked) * 100) : 0,
    keys: minutes.reduce((n, x) => n + x.k, 0),
    mouse: minutes.reduce((n, x) => n + x.m, 0),
  };
}
